import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/footer.css";

const Footer = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [dataNodes, setDataNodes] = useState([]);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Initialize neural network nodes
  useEffect(() => {
    const nodes = [];
    for (let i = 0; i < 25; i++) {
      nodes.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        connections: [],
        pulse: Math.random() * Math.PI * 2,
        active: false
      });
    }
    
    // Create connections between nearby nodes
    nodes.forEach((node, i) => {
      nodes.forEach((otherNode, j) => {
        if (i !== j) {
          const distance = Math.sqrt(
            Math.pow(node.x - otherNode.x, 2) + 
            Math.pow(node.y - otherNode.y, 2)
          );
          if (distance < 30 && node.connections.length < 4) {
            node.connections.push(j);
          }
        }
      });
    });
    
    setDataNodes(nodes);
  }, []);

  // Neural network animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    const animate = () => {
      canvas.width = rect.width;
      canvas.height = rect.height;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw nodes
      setDataNodes(prevNodes => {
        const updatedNodes = prevNodes.map(node => {
          // Update position
          node.x += node.vx;
          node.y += node.vy;
          
          // Bounce off edges
          if (node.x <= 0 || node.x >= 100) node.vx *= -1;
          if (node.y <= 0 || node.y >= 100) node.vy *= -1;
          
          // Keep in bounds
          node.x = Math.max(0, Math.min(100, node.x));
          node.y = Math.max(0, Math.min(100, node.y));
          
          // Update pulse
          node.pulse += 0.1;
          
          // Check if near mouse
          const nodeScreenX = (node.x / 100) * canvas.width;
          const nodeScreenY = (node.y / 100) * canvas.height;
          const mouseDistance = Math.sqrt(
            Math.pow(nodeScreenX - mousePos.x, 2) + 
            Math.pow(nodeScreenY - mousePos.y, 2)
          );
          node.active = mouseDistance < 100;
          
          return node;
        });

        // Draw connections
        updatedNodes.forEach(node => {
          const nodeX = (node.x / 100) * canvas.width;
          const nodeY = (node.y / 100) * canvas.height;
          
          node.connections.forEach(connectionId => {
            const connectedNode = updatedNodes[connectionId];
            if (connectedNode) {
              const connectedX = (connectedNode.x / 100) * canvas.width;
              const connectedY = (connectedNode.y / 100) * canvas.height;
              
              // Data flow animation
              const flowProgress = (Math.sin(Date.now() * 0.005 + node.id) + 1) / 2;
              const flowX = nodeX + (connectedX - nodeX) * flowProgress;
              const flowY = nodeY + (connectedY - nodeY) * flowProgress;
              
              // Connection line
              ctx.beginPath();
              ctx.moveTo(nodeX, nodeY);
              ctx.lineTo(connectedX, connectedY);
              ctx.strokeStyle = node.active || connectedNode.active 
                ? 'rgba(0, 255, 255, 0.6)' 
                : 'rgba(0, 255, 255, 0.2)';
              ctx.lineWidth = node.active || connectedNode.active ? 2 : 1;
              ctx.stroke();
              
              // Data packet
              ctx.beginPath();
              ctx.arc(flowX, flowY, 3, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(255, 140, 0, 0.8)';
              ctx.fill();
              ctx.shadowBlur = 10;
              ctx.shadowColor = '#FF8C00';
              ctx.fill();
              ctx.shadowBlur = 0;
            }
          });
        });

        // Draw nodes
        updatedNodes.forEach(node => {
          const nodeX = (node.x / 100) * canvas.width;
          const nodeY = (node.y / 100) * canvas.height;
          const pulseSize = 3 + Math.sin(node.pulse) * 2;
          
          // Node glow
          ctx.beginPath();
          ctx.arc(nodeX, nodeY, pulseSize + 5, 0, Math.PI * 2);
          ctx.fillStyle = node.active 
            ? 'rgba(0, 255, 255, 0.3)' 
            : 'rgba(0, 255, 255, 0.1)';
          ctx.fill();
          
          // Node core
          ctx.beginPath();
          ctx.arc(nodeX, nodeY, pulseSize, 0, Math.PI * 2);
          ctx.fillStyle = node.active ? '#00FFFF' : '#1E90FF';
          ctx.fill();
        });

        return updatedNodes;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mousePos]);

  // Mouse tracking
  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  return (
    <footer className="colee-footer-futuristic">
      {/* Neural Network Canvas */}
      <canvas
        ref={canvasRef}
        className="colee-neural-network"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      
      {/* Cyber Grid Background */}
      <div className="colee-cyber-grid"></div>
      
      {/* Quantum Particles */}
      <div className="colee-quantum-field">
        {[...Array(15)].map((_, i) => (
          <div key={i} className={`colee-quantum-particle colee-particle-${i + 1}`}></div>
        ))}
      </div>

      <div className="container position-relative">
        <div className="row justify-content-center text-center">
          
          {/* Main Content Section */}
          <div className="col-12 col-md-8 mb-4">
            <h5 className="colee-footer-title colee-neon-text">
              <span className="colee-flicker">COLEE</span> 
              <span className="colee-neural-pulse">Software Services</span>
            </h5>
            <p className="colee-footer-description">
              Empowering businesses 
              <span className="colee-highlight-pulse"> with personalized solutions</span>.
              <br />
              <span className="colee-data-stream">Contact us to learn how we can help your business</span>
              <span className="colee-neon-text-subtle"> transcend digital boundaries</span>.
            </p>
            
            {/* Revolutionary Feature: Real-time System Status */}
            <div className="colee-system-status">
              <div className="colee-status-grid">
                <div className="colee-status-item">
                  <span className="colee-status-label">Processing</span>
                  <div className="colee-status-bar">
                    <div className="colee-status-fill colee-processing"></div>
                  </div>
                  <span className="colee-status-value">98.7%</span>
                </div>
                <div className="colee-status-item">
                  <span className="colee-status-label">Sync</span>
                  <div className="colee-status-bar">
                    <div className="colee-status-fill colee-quantum"></div>
                  </div>
                  <span className="colee-status-value">100%</span>
                </div>
                <div className="colee-status-item">
                  <span className="colee-status-label">Data Flow</span>
                  <div className="colee-status-bar">
                    <div className="colee-status-fill colee-dataflow"></div>
                  </div>
                  <span className="colee-status-value">94.3%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Social Hub */}
          <div className="col-12">
            <h5 className="colee-footer-title colee-neon-text">
              Where Your Digital Presence Is Mastered 
            </h5>
            <div className="colee-footer-social-icons">
              <a
                href="https://github.com/CorwinHiatt"
                className="colee-social-link colee-neon-icon colee-github"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-github"></i>
                <div className="colee-social-pulse"></div>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61572314462403"
                className="colee-social-link colee-neon-icon colee-facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-facebook"></i>
                <div className="colee-social-pulse"></div>
              </a>
              <a
                href="https://x.com/ColeeSoftware"
                className="colee-social-link colee-neon-icon colee-twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-twitter-x"></i>
                <div className="colee-social-pulse"></div>
              </a>
              <a
                href="https://www.instagram.com/colee_software/"
                className="colee-social-link colee-neon-icon colee-instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-instagram"></i>
                <div className="colee-social-pulse"></div>
              </a>
            </div>
            
            {/* Interaction Hint */}
            <p className="colee-interaction-hint">
              <i className="bi bi-mouse"></i> 
            </p>
          </div>
        </div>
        
        <div className="text-center mt-4">
          <p className="colee-footer-copyright">
            <span className="colee-neon-text-subtle">&copy; {new Date().getFullYear()}</span>
            <span className="colee-highlight-pulse"> COLEE Software Services</span>
            <span className="colee-neon-text-subtle">. All rights reserved.</span>
            <br />
            <span className="colee-quantum-signature">
              Please put privacy and accessiblity statement links here 
            

            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;